// @flow

import React from 'react';
import useSheet from 'app/jss';
import classNames from 'classnames';
import * as cssUtils from 'app/utils/cssUtils';
import { noop } from 'lodash';
import colorUtils from 'color';
import * as Enter from 'app/utils/animations/enter';
import * as Leave from 'app/utils/animations/leave';
import Tappable from 'app/components/commons/Tappable';
import Icon from 'app/components/commons/Icon';
import AnimatedGroup from 'app/components/commons/AnimatedGroup';
import AnimatedGroupChild from 'app/components/commons/AnimatedGroupChild';

type Props = {
  classes: Object,
  onClear?: () => void,
  onTextChange?: (text: string) => void,
  onFocus?: () => void,
  onBlur?: () => void,
  placeholder?: string,
  name?: string,
  onSubmit?: () => void,
  onEscape?: () => void,
  placeholderIcon?: string,
  text?: string,
  scheme?: 'dark' | 'light',
  condensed?: boolean,
  className?: string,
  uppercase?: boolean,
  autoFocus?: boolean,
  // if turned on, only uses the placeholder as a placeholder
  // doesn't move it above the text box
  placeholderOnly?: boolean,
  focusDelay?: number,
  editMode?: boolean,
  disabled?: boolean,
  inputClasses?: string,
  invalid?: boolean,
  invalidReason?: string,
};

type State = {
  text: string,
  hasFocus: boolean,
};

class FriendTextInput extends React.Component {
  props: Props;

  constructor(props: Props) {
    super(props);
    this.state = {
      text: props.text,
      hasFocus: false,
    };
  }

  static defaultProps = {
    onClear: noop,
    onTextChange: noop,
    placeholder: undefined,
    invalidReason: undefined,
    inputClasses: undefined,
    name: 'textInput',
    onFocus: noop,
    onBlur: noop,
    onSubmit: noop,
    onEscape: noop,
    placeholderIcon: undefined,
    text: '',
    scheme: 'dark',
    condensed: false,
    className: undefined,
    uppercase: false,
    autoFocus: false,
    placeholderOnly: false,
    focusDelay: 0,
    editMode: true,
    disabled: false,
    invalid: false,
  };

  componentDidMount() {
    if (this.props.autoFocus) {
      // sometimes animations and such are going on
      // this makes the element display: none and you can't focus
      // an element that isn't visible. So we accept a focus delay
      // to wait until such a time as the element should be visible
      setTimeout(() => {
        if (this.inputField) {
          this.inputField.focus();
        }
      }, this.props.focusDelay);
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const { text } = this.state;
    if (nextProps.text !== this.props.text && nextProps.text !== text) {
      this.setState({ text: nextProps.text });
    }
  }

  componentDidUpdate(prevProps) {
    const turnedEditOn = this.props.editMode && this.props.editMode !== prevProps.editMode;
    const turnedEditOff = !this.props.editMode && this.props.editMode !== prevProps.editMode;
    if (turnedEditOn) {
      this.inputField.focus();
    } else if (turnedEditOff) {
      this.inputField.blur();
    }
  }

  handleInputChange = (value) => {
    this.props.onTextChange(value);
    this.setState({
      text: value,
    });
  };

  onKeyDown = (e: React.KeyboardEvent<*>) => {
    if (e.keyCode === 13) {
      if (this.props.onSubmit) {
        this.props.onSubmit(this.state.text);
      }
    }
    if (e.keyCode === 27 && this.state.hasFocus) {
      this.props.onEscape(this.inputField);
    }
  };

  setInputRef = (node) => {
    this.inputField = node;
  };

  inputFieldOnFocus = () => {
    if (!this.state.text) {
      this.handleInputChange('');
    }
    this.setState({ hasFocus: true });
    this.props.onFocus();
  };

  inputFieldOnBlur = () => {
    this.setState({ hasFocus: false });
    this.props.onBlur(this.state.text);
  };

  clearText = () => {
    this.handleInputChange('');
    this.inputField.focus();
    this.props.onClear();
  };

  buildEndIcon = () => {
    const { invalid, placeholderIcon, classes } = this.props;
    const { text } = this.state;

    const hasText = !!text;
    let icon = placeholderIcon;
    let onTap;
    if (hasText) {
      icon = 'icon-cross';
      onTap = this.clearText;
    }
    if (invalid) {
      icon = 'icon-alert';
      onTap = this.clearText;
    }

    if (icon) {
      return (
        <Tappable
          component="div"
          tabIndex={onTap ? 0 : -1}
          onTap={onTap}
          preventDefault
          stopPropagation
          key={icon}
          className={classNames(classes.icon, {
            placeholderIcon: !hasText && !invalid,
            clearIcon: hasText && !invalid,
            invalid,
          })}
        >
          <Icon type={icon} />
        </Tappable>
      );
    }
    return null;
  };

  render() {
    const {
      classes,
      placeholder,
      name,
      onSubmit,
      scheme,
      condensed,
      className,
      uppercase,
      placeholderOnly,
      editMode,
      disabled,
      inputClasses,
      invalid,
      invalidReason,
    } = this.props;

    const { text, hasFocus } = this.state;

    const hasText = !!text;
    const inputPlaceholder = !placeholderOnly || !editMode ? undefined : placeholder;
    return (
      <AnimatedGroup
        className={classNames(classes.text, className, classes[scheme], {
          focused: hasFocus,
          condensed,
          invalid,
          editMode,
        })}
      >
        <div className={classes.innerInput}>
          <input
            ref={this.setInputRef}
            name={name}
            type="text"
            value={text}
            className={classNames(classes.textInput, inputClasses, {
              uppercase,
              static: !editMode,
            })}
            onChange={e => this.handleInputChange(e.target.value)}
            onFocus={this.inputFieldOnFocus}
            onBlur={this.inputFieldOnBlur}
            onSubmit={() => onSubmit(text)}
            onKeyDown={this.onKeyDown}
            placeholder={inputPlaceholder}
            disabled={disabled}
          />

          {!placeholderOnly && (
            <span className={classNames(classes.placeholder, { above: hasFocus || hasText })}>
              {placeholder}
            </span>
          )}
        </div>
        {editMode && (
          <AnimatedGroup onEnter={Enter.scaleUp(0.2, 0.2)} onLeave={Leave.scaleDown(0.2)}>
            {this.buildEndIcon()}
          </AnimatedGroup>
        )}
        <div className={classNames(classes.underline, { visible: editMode })} />
        {invalid &&
          invalidReason && (
            <AnimatedGroupChild
              onEnter={Enter.slideUpAndAppear()}
              onLeave={Leave.slideDownAndDisappear()}
            >
              <div className={classes.invalidReason}>{invalidReason}</div>
            </AnimatedGroupChild>
          )}
      </AnimatedGroup>
    );
  }
}

function applyStyles(theme: Theme) {
  return {
    invalidReason: {
      fontSize: 10,
      fontWeight: 'normal',
      fontSmoothing: 'antialiased',
      textTransform: 'uppercase',
      position: 'absolute',
      bottom: -17,
      left: 0,
      right: 0,
      textAlign: 'center',
      color: theme.color.danger,
    },
    text: {
      display: 'flex',
      position: 'relative',
      flexDirection: 'column',
      '&.condensed': {
        '&.editMode': {
          paddingRight: 20,
        },
        '& $textInput': {
          padding: 0,
          overflow: 'hidden',
        },
        '& $placeholder': {
          bottom: 0,
          '&.above': {
            transform: 'translateY(-1em) scale(0.6)',
          },
        },
        '& $underline': {
          marginTop: 2,
        },
      },
    },
    underline: {
      height: 1,
      position: 'absolute',
      bottom: -2,
      left: 0,
      width: 0,
      transition: 'background-color 0.2s, width 0.3s ease-out',
      '&.visible': {
        width: '100%',
        transition: 'background-color 0.2s, width 0.3s ease-in',
      },
    },
    dark: {
      color: theme.color.basis2,
      '& $textInput': {
        color: theme.color.basis2,
      },
      '& $placeholder': {
        color: theme.color.basis4,
      },
      // invlid styles
      '&.invalid': {
        '& $icon': {
          color: theme.color.danger,
        },
      },
      // focused styles
      '&.focused': {
        '& $underline': {
          backgroundColor: theme.color.basis6,
        },
        '& $icon': {
          color: theme.color.basis6,
        },
      },
      '& .clearIcon': {
        color: theme.color.basis4,
        cursor: 'pointer',
        zIndex: 1,
        transition: 'color 0.2s',
        ...cssUtils.hover({
          color: theme.color.basis3,
        }),
      },
      '& .placeholderIcon': {
        color: theme.color.basis4,
      },
      '& $underline': {
        backgroundColor: theme.color.basis6,
      },
    },
    light: {
      color: theme.color.basis4,
      '&.invalid': {
        '& $icon': {
          color: theme.color.danger,
        },
      },
      '&.focused': {
        '& $textInput': {
          color: theme.color.white,
        },
        '& $underline': {
          backgroundColor: theme.color.white,
        },
        '& $placeholder': {
          color: theme.color.basis4,
        },
        '& $icon': {
          color: theme.color.basis4,
        },
      },
      '& .clearIcon': {
        color: theme.color.basis4,
        cursor: 'pointer',
        zIndex: 1,
        transition: 'color 0.2s',
        ...cssUtils.hover({
          color: theme.color.white,
        }),
      },
      '& .placeholderIcon': {
        color: theme.color.basis4,
      },
      '& $underline': {
        backgroundColor: theme.color.basis4,
      },
    },
    innerInput: {
      position: 'relative',
      alignItems: 'center',
      display: 'flex',
      flexDirection: 'row',
    },
    textInput: {
      backgroundColor: 'transparent',
      border: 'none',
      borderRadius: 0,
      boxShadow: 'none',
      fontSize: '1em',
      paddingRight: 10,
      paddingTop: theme.spacing.xsmall,
      width: '100%',
      '$dark &::placeholder': {
        color: colorUtils(theme.color.basis4)
          .alpha(0.4)
          .toString(),
      },
      '$light &::placeholder': {
        color: colorUtils(theme.color.basis4)
          .alpha(0.4)
          .toString(),
      },
      '&.static': {
        textOverflow: 'ellipsis',
      },
      '&:focus': {
        outline: 'none',
      },
      '&.uppercase': {
        textTransform: 'uppercase',
      },
    },
    icon: {
      transition: 'opacity 0.2s, color 0.2s',
      fontSize: '0.8em',
      position: 'absolute',
      right: 0,
      top: 0,
      bottom: 0,
      display: 'flex',
      alignItems: 'center',
    },
    placeholder: {
      fontSize: '1em',
      position: 'absolute',
      bottom: 4,
      left: 0,
      transform: 'translate(0, 0) scale(1)',
      transformOrigin: 0,
      transition: 'transform 0.2s ease-in-out, color 0.2s',
      pointerEvents: 'none',
      fontWeight: 600,
      whiteSpace: 'nowrap',
      fontSmoothing: 'antialiased',
      '&.uppercase': {
        textTransform: 'uppercase',
      },
      '&.above': {
        textTransform: 'uppercase',
        transform: 'translateY(-75%) scale(0.6)',
        '&.placeholderOnly': {
          display: 'none',
        },
      },
    },
  };
}

export default useSheet(applyStyles)(FriendTextInput);
